import React, { useEffect, useMemo, useState } from 'react';
import { Bubble, List, Select } from 'tea-component';
import { getLocale, useIntl } from 'umi';
import { ServerTimeType, SingleCommonType } from '../../types';
import SvgIcon from '@/pages/home/components/SvgIcon';
import './index.scss';
import RenderGameIcon from '@op/components/RenderImg/renderGameIcon';
import RangePicker from '@home/components/RangePicker';
import { dailyFormat, dailyToDay, dayToDaily, timeRangeUserTranslate } from '../../constants/date';
import { useGameAndTimeDefaultValue } from '../../hooks/init';
import moment, { Moment } from 'moment';
import { commonBackInfo } from '../../utils';
import { arrayToObj } from '@op/utils';
import useHistoryFilter from '@op/hooks/useHistoryFilter';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { formatTimeToServer } from '@op/utils/time';

export default function ManyCompareGameSelect({
  filterOptions,
  filterValue,
  defaultValue,
  onChange,
  gameInfo,
}: SingleCommonType) {
  const { formatMessage } = useIntl();

  const { addCompetitorFilter } = useHistoryFilter({ gameInfo });
  const { competitors, gameData } = useMemo(() => {
    if (!filterOptions) return { competitors: [], gameData: {} };

    const { competitors, main } = filterOptions?.ori_competitor_games || {};

    const list = [...(main ? [main] : []), ...(competitors?.infos || [])];

    return {
      competitors: list,
      gameData: arrayToObj(list, 'edition_unified_id'),
    };
  }, []);

  const {
    dateType,
    dateTypeRef,
    setDateType,
    gameInfoAndTime,
    setGameInfoAndTime,
    value,
    syncValue,
  } = useGameAndTimeDefaultValue({
    filterValue,
    defaultValue: filterOptions,
    gameInfo,
    allGameList: competitors.map((item) => item.edition_unified_id),
  });

  const options = useMemo(() => {
    function getTextElement(item: any) {
      return (
        <div className="dbo-flex-h100">
          <RenderGameIcon
            className="dbo-game-icon"
            gameInfo={item}
          ></RenderGameIcon>
          <span className="dbo-game-title">{item.entity_name}</span>
        </div>
      );
    }

    return competitors.map((item: any) => ({
      text: getTextElement(item),
      value: item.edition_unified_id,
    }));
  }, [competitors]);

  function dealwithDate(data: Array<{ gameId: string; times: Array<string>; }>) {
    const result = data
      .map(({ gameId, times }) => {
        if (!gameId && times.length === 0) return null;
        const { id_type, entity_type } = gameData?.[gameId] || {};
        const realTime = formatTimeToServer(
          times as any,
          dateTypeRef.current as any,
        );
        return {
          id_type,
          entity_type,
          edition_unified_id: gameId,
          start_time: realTime[0],
          end_time: realTime[1],
          date_type: dateTypeRef.current,
        };
      })
      .filter(Boolean);

    return { compare_games: result };
  }

  function changeComparison(
    index: number,
    data: { gameId: string; times: Array<string>; },
    isClear = false,
  ) {
    if ((data.gameId && data.times.length > 0) || isClear) {
      gameInfoAndTime[index] = data;
      // 伪更新gameInfoAndTime
      setGameInfoAndTime(gameInfoAndTime);
      // 真更新value
      syncValue([...gameInfoAndTime]);

      const result = dealwithDate(gameInfoAndTime);
      addCompetitorFilter(result);

      if (!isClear) {
        buriedBtnPoint('ChooseGame', { value: data });
      }
    }
  }

  useEffect(() => {
    const result = dealwithDate(value);

    onChange?.(
      'compare_games',
      commonBackInfo({ ...result, gameData, dateType }, result),
    );
  }, [value, gameData]);

  return (
    <div className="many-compare-game-select-wrapper">
      <h4>
        {formatMessage({
          id: 'Set Comparison Time (Up to 4 games can be compared)',
        })}
      </h4>
      <List className="many-compare-game-select-list">
        {gameInfoAndTime.map((item, index) => (
          <GameSelectItem
            options={options}
            isDisable={index === 0}
            dateType={dateType}
            key={item.gameId + index}
            item={item}
            setDateType={setDateType}
            onChange={(value, isClear) =>
              changeComparison(index, value, isClear)
            }
            mainGameInfoAndTime={gameInfoAndTime[0]}
          ></GameSelectItem>
        ))}
      </List>
    </div>
  );
}

function GameSelectItem({
  options,
  dateType,
  setDateType,
  isDisable,
  item,
  onChange,
  mainGameInfoAndTime,
}: {
  options: Array<{ text: any; value: string; }>;
  dateType: ServerTimeType;
  setDateType: (value: ServerTimeType) => void;
  isDisable?: boolean;
  item: { gameId: string; times: string[]; };
  onChange: (
    value: { gameId: string; times: string[]; },
    isClear?: boolean,
  ) => void;
  mainGameInfoAndTime: { gameId: string; times: string[]; };
}) {
  const [gameId, setGameId] = useState('');
  const [time, setTime] = useState<Array<string>>([]);
  const { formatMessage } = useIntl();

  const rangePickerValue = useMemo(() => time.map((time) => moment(time)), [time]);

  useEffect(() => {
    const { times, gameId } = item;

    setGameId(gameId);
    setTime(times);
  }, [item]);

  // 获取时间的开始和结束时间 如果改变日期，这里value会返回[null, null]
  const onChangeRangePicker = (value: Array<Moment>, type: string) => {
    const localDateType = dayToDaily[type];
    setDateType(localDateType);
    const hasTime = value.every(Boolean) && value.length > 0;

    let timeValue: Array<string> = time;
    if (hasTime) {
      timeValue = value.map((item) => (typeof item === 'string' ? moment(item) : item).format(
        dailyFormat[localDateType],
      ));
      setTime(timeValue);
    }

    const dateValue = formatTimeToServer(timeValue as any, dateType as any);
    onChange({ gameId, times: dateValue });
  };

  const showDeleteIcon = useMemo(() => !isDisable && (gameId || time.length > 0), [gameId, time]);

  const { times: mainGameTimes } = mainGameInfoAndTime;

  return (
    <List.Item>
      <Select
        appearance="button"
        options={options}
        value={gameId}
        disabled={isDisable}
        onChange={(value) => {
          setGameId(value);
          const dateValue = formatTimeToServer(time as any, dateType as any);
          onChange({
            gameId: value,
            times:
              dateValue.length > 0
                ? dateValue
                : formatTimeToServer(mainGameTimes as any, dateType as any),
          });
        }}
        placeholder={formatMessage({ id: 'Please select a game' })}
        matchButtonWidth
        listHeight={270}
      />

      <RangePicker
        startOfTime="2017-01-01"
        className="search-border"
        typeList={['hour', 'date', 'week', 'month']}
        showSelectType={true}
        startOfWeek={1}
        language={getLocale() === 'zh-CN' ? 'cn' : 'en'}
        value={rangePickerValue}
        type={dailyToDay[dateType]}
        onChange={onChangeRangePicker}
        userTranslation={timeRangeUserTranslate}
      />
      {showDeleteIcon && (
        <Bubble content={formatMessage({ id: 'Clear game and date' })}>
          <span
            className="dbo-clear-button"
            onClick={() => {
              setGameId('');
              setTime([]);
              onChange({ gameId: '', times: [] }, true);
              buriedBtnPoint('DeleteGame', { value: { gameId, times: time } });
            }}
          >
            <SvgIcon type="clear-icon" />
          </span>
        </Bubble>
      )}
    </List.Item>
  );
}
